import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';

function AgriOperations({ filters, loading, setLoading, isStaging, onDrilldown }) {
  const [urgentIssues, setUrgentIssues] = useState([]);
  const [demandTrend, setDemandTrend] = useState({ labels: [], data: [] });
  const [demandAlerts, setDemandAlerts] = useState([]);
  const [cropPestHeatmap, setCropPestHeatmap] = useState({ crops: [], pests: [], values: [] });
  const [problemTrend, setProblemTrend] = useState({ labels: [], datasets: [] });
  const [problemSentiment, setProblemSentiment] = useState({ labels: [], datasets: [] });
  const [cropKeywords, setCropKeywords] = useState([]);
  const [solutionFlow, setSolutionFlow] = useState({ nodes: [], links: [] });
  const [solutionEffectiveness, setSolutionEffectiveness] = useState({ labels: [], data: [] });
  const [solutionSentiment, setSolutionSentiment] = useState({ labels: [], data: [] });
  const [sentimentByCrop, setSentimentByCrop] = useState({ labels: [], datasets: [] });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });

    Promise.all([
      axios.get(`/api/agri/operations/urgent-issues?${params}`),
      axios.get(`/api/agri/operations/demand-signal-trend?${params}`),
      axios.get(`/api/agri/operations/demand-change-alert?${params}`),
      axios.get(`/api/agri/operations/crop-pest-heatmap?${params}`),
      axios.get(`/api/agri/operations/problem-trend?${params}`),
      axios.get(`/api/agri/operations/problem-sentiment?${params}`),
      axios.get(`/api/agri/operations/crop-keywords?${params}`),
      axios.get(`/api/agri/operations/solution-flow?${params}`),
      axios.get(`/api/agri/operations/solution-effectiveness?${params}`),
      axios.get(`/api/agri/operations/solution-sentiment?${params}`),
      axios.get(`/api/agri/operations/sentiment-by-crop?${params}`),
    ])
      .then(([uiRes, dtRes, daRes, cpRes, ptRes, psRes, ckRes, sfRes, seRes, ssRes, scRes]) => {
        setUrgentIssues(uiRes.data.data || []);
        setDemandTrend(dtRes.data);
        setDemandAlerts(daRes.data.alerts || []);
        setCropPestHeatmap(cpRes.data);
        setProblemTrend(ptRes.data);
        setProblemSentiment(psRes.data);
        setCropKeywords(ckRes.data || []);
        setSolutionFlow(sfRes.data);
        setSolutionEffectiveness(seRes.data);
        setSolutionSentiment(ssRes.data);
        setSentimentByCrop(scRes.data);
      })
      .catch(err => console.error('Error loading Agri Operations:', err))
      .finally(() => setLoading(false));
  }, [filters, setLoading]);

  const cardStyle = {
    background: '#fff', borderRadius: 8, padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };
  const gridStyle = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: 16,
  };
  const plotLayout = { margin: { t: 30, b: 40, l: 50, r: 20 }, height: 300, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent' };
  const colors = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
  const sentimentColors = { Positive: '#2ecc71', Neutral: '#f39c12', Negative: '#e74c3c' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
  const thStyle = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #ddd', fontWeight: 600 };
  const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #eee' };

  return (
    <div style={gridStyle}>
      {/* Urgent Issues */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Urgent Issues</h3>
        {urgentIssues.length > 0 ? (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Topic</th>
                  <th style={thStyle}>Urgency</th>
                  <th style={thStyle}>Sentiment</th>
                  <th style={thStyle}>Date</th>
                </tr>
              </thead>
              <tbody>
                {urgentIssues.map((issue, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{issue.primary_topic || 'N/A'}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: issue.urgency === 'critical' ? '#ffe0e0' : '#fff3cd',
                        color: issue.urgency === 'critical' ? '#c0392b' : '#856404',
                      }}>
                        {issue.urgency}
                      </span>
                    </td>
                    <td style={tdStyle}>{issue.overall_sentiment || 'N/A'}</td>
                    <td style={tdStyle}>{issue.created_at ? new Date(issue.created_at).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No urgent issues</div>
        )}
      </div>

      {/* Demand Signal Trend — single series line chart */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Demand Signal Trend</h3>
        {(demandTrend.labels || []).length > 0 ? (
          <Plot
            data={[{
              x: demandTrend.labels,
              y: demandTrend.data,
              type: 'scatter',
              mode: 'lines+markers',
              name: 'Demand Signal',
              line: { color: '#3498db' },
              fill: 'tozeroy',
              fillcolor: 'rgba(52,152,219,0.1)',
            }]}
            layout={plotLayout}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No trend data</div>
        )}
      </div>

      {/* Demand Change Alerts — table */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Demand Change Alerts</h3>
        {demandAlerts.length > 0 ? (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Crop</th>
                  <th style={thStyle}>Demand</th>
                  <th style={thStyle}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {demandAlerts.map((alert, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{alert.crop_name}</td>
                    <td style={tdStyle}>{alert.current_demand}</td>
                    <td style={tdStyle}>{alert.trend}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No alerts</div>
        )}
      </div>

      {/* Crop-Pest Heatmap */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Crop-Pest Heatmap</h3>
        {(cropPestHeatmap.crops || []).length > 0 ? (
          <Plot
            data={[{
              z: cropPestHeatmap.values,
              x: cropPestHeatmap.pests,
              y: cropPestHeatmap.crops,
              type: 'heatmap',
              colorscale: 'YlOrRd',
            }]}
            layout={{ ...plotLayout, height: Math.max(300, (cropPestHeatmap.crops || []).length * 30) }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No heatmap data</div>
        )}
      </div>

      {/* Problem Trend — multi-series */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Problem Trend</h3>
        <Plot
          data={(problemTrend.datasets || []).map((ds, i) => ({
            x: problemTrend.labels,
            y: ds.data,
            type: 'scatter',
            mode: 'lines+markers',
            name: ds.label,
            line: { color: colors[i % colors.length] },
          }))}
          layout={plotLayout}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
        />
      </div>

      {/* Problem Sentiment — grouped bar (topics × positive/neutral/negative) */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Problem Sentiment</h3>
        {(problemSentiment.labels || []).length > 0 ? (
          <Plot
            data={(problemSentiment.datasets || []).map((ds) => ({
              x: problemSentiment.labels,
              y: ds.data,
              type: 'bar',
              name: ds.label,
              marker: { color: sentimentColors[ds.label] || '#999' },
            }))}
            layout={{ ...plotLayout, barmode: 'group', xaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Crop Keywords — horizontal bar */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Crop Keywords</h3>
        {cropKeywords.length > 0 ? (
          <Plot
            data={[{
              x: cropKeywords.map(k => k.size),
              y: cropKeywords.map(k => k.text),
              type: 'bar',
              orientation: 'h',
              marker: { color: '#2ecc71' },
            }]}
            layout={{ ...plotLayout, height: Math.max(300, cropKeywords.length * 25), yaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No keyword data</div>
        )}
      </div>

      {/* Solution Flow (Sankey) */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Solution Flow</h3>
        {(solutionFlow.nodes || []).length > 0 ? (
          <Plot
            data={[{
              type: 'sankey',
              node: {
                label: solutionFlow.nodes.map(n => n.name),
                color: solutionFlow.nodes.map((_, i) => colors[i % colors.length]),
              },
              link: {
                source: solutionFlow.links.map(l => l.source),
                target: solutionFlow.links.map(l => l.target),
                value: solutionFlow.links.map(l => l.value),
              },
            }]}
            layout={{ ...plotLayout, height: 400 }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No flow data</div>
        )}
      </div>

      {/* Solution Effectiveness — vertical bar */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Solution Effectiveness</h3>
        {(solutionEffectiveness.labels || []).length > 0 ? (
          <Plot
            data={[{
              x: solutionEffectiveness.labels,
              y: solutionEffectiveness.data,
              type: 'bar',
              marker: { color: '#3498db' },
            }]}
            layout={{ ...plotLayout, xaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Solution Sentiment — single series line */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Solution Sentiment</h3>
        {(solutionSentiment.labels || []).length > 0 ? (
          <Plot
            data={[{
              x: solutionSentiment.labels,
              y: solutionSentiment.data,
              type: 'scatter',
              mode: 'lines+markers',
              name: 'Sentiment',
              line: { color: '#9b59b6' },
            }]}
            layout={{ ...plotLayout, yaxis: { title: 'Sentiment', range: [0, 100] } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Sentiment by Crop — grouped bar */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Sentiment by Crop</h3>
        {(sentimentByCrop.labels || []).length > 0 ? (
          <Plot
            data={(sentimentByCrop.datasets || []).map((ds) => ({
              x: sentimentByCrop.labels,
              y: ds.data,
              type: 'bar',
              name: ds.label,
              marker: { color: sentimentColors[ds.label] || '#999' },
            }))}
            layout={{ ...plotLayout, barmode: 'group', xaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>
    </div>
  );
}

export default AgriOperations;
